import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  Signal,
  signal,
} from '@angular/core';
import { OrdersQueryParams, SortingOption } from '@app/utils/interfaces/order.interface';
import { TableSortComponent } from '../table-sort/table-sort.component';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import {
  DATE_CREATED_FILTER_OPTIONS,
  DELIVERY_TYPE_FILTER_OPTIONS,
  ORDER_FILTERS_CONFIG,
  ORDER_SORT_OPTIONS,
  ORDER_STATUS_FILTER_OPTIONS,
} from '@app/utils/constants/orders.constants';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OrdersService } from '../../services/orders.service';
import { OrderStatus } from '@app/utils/enums/orders-history.enum';
import { OrderDeliveryTypeEnum } from '@app/utils/enums/order-delivery-type-enum';
import { map } from 'rxjs';
import { DateRangeFilterComponent } from '@app/components/shared/date-range-filter/date-range-filter.component';
import {
  IDateRangeFilterSelection,
  IFilterOptions,
  IToggleOptionParams,
} from '@app/utils/interfaces/orders.interface';
import { OrderDateCreatedEnum, OrderFiltersEnum } from '@app/utils/enums/orders.enum';
import { OrderFilterService } from '../../services/order-filter/order-filter.service';

@Component({
  selector: 'app-order-filters',
  standalone: true,
  imports: [
    CommonModule,
    TableSortComponent,
    TranslatePipe,
    MatIcon,
    ReactiveFormsModule,
    MatCheckboxModule,
    DateRangeFilterComponent,
  ],
  templateUrl: './order-filters.component.html',
  styleUrl: './order-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderFiltersComponent {
  public isOpen = input<boolean>(false);
  public closePanel = output<void>();
  public filtersApplied = output<void>();
  public sortApplied = output<SortingOption[]>();

  public filterSortKey: string | null = 'id-asc';

  public filterConfig = ORDER_FILTERS_CONFIG;
  public orderFilterSections = OrderFiltersEnum;

  public selectedFilters = signal({
    [OrderFiltersEnum.CREATOR]: [] as string[],
    [OrderFiltersEnum.STATUS]: [] as string[],
    [OrderFiltersEnum.DELIVERY_TYPE]: [] as string[],
    [OrderFiltersEnum.DATE_CREATED]: [] as IDateRangeFilterSelection[],
  });

  private activeSorting: SortingOption[] =
    ORDER_SORT_OPTIONS.find((option) => option.key === 'id-asc')?.sorting || [];

  private readonly ordersService = inject(OrdersService);
  private readonly ordersFilterService = inject(OrderFilterService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly activeFilters = toSignal(
    this.ordersFilterService.activeFilters$.pipe(takeUntilDestroyed(this.destroyRef)),
    { initialValue: {} },
  );

  private readonly defaultFilters = {
    [OrderFiltersEnum.CREATOR]: [],
    [OrderFiltersEnum.STATUS]: [],
    [OrderFiltersEnum.DELIVERY_TYPE]: [],
    [OrderFiltersEnum.DATE_CREATED]: [],
  };

  private readonly creatorFilterOptions = toSignal(
    this.ordersService.getDistinctCreators().pipe(
      map((result) => {
        return result.map((item) => ({ key: item, label: item }));
      }),
    ),
    { initialValue: [] },
  );
  private readonly statusFilterOptions = signal(ORDER_STATUS_FILTER_OPTIONS);
  private readonly deliveryTypeFilterOptions = signal(DELIVERY_TYPE_FILTER_OPTIONS);
  private readonly dateCreatedFilterOptions = signal(DATE_CREATED_FILTER_OPTIONS);

  public filterOptions: Record<OrderFiltersEnum, Signal<IFilterOptions[]>> = {
    [OrderFiltersEnum.CREATOR]: this.creatorFilterOptions,
    [OrderFiltersEnum.STATUS]: this.statusFilterOptions,
    [OrderFiltersEnum.DELIVERY_TYPE]: this.deliveryTypeFilterOptions,
    [OrderFiltersEnum.DATE_CREATED]: this.dateCreatedFilterOptions,
  };

  constructor() {
    effect(
      () => {
        const isOpen = this.isOpen();
        const filters = this.activeFilters();

        if (!isOpen) return;

        this.syncFromAppliedFilters(filters);
      },
      { allowSignalWrites: true },
    );
  }

  public toggleOption({ section, option, checked, dateRangeSelection }: IToggleOptionParams): void {
    this.selectedFilters.update((filters) => {
      if (section === OrderFiltersEnum.DATE_CREATED) {
        const hasValidRange = dateRangeSelection?.optionKey;
        return { ...filters, [section]: hasValidRange ? [dateRangeSelection] : [] };
      }

      return {
        ...filters,
        [section]: checked
          ? [...filters[section], option]
          : filters[section].filter((v) => v !== option),
      };
    });
  }

  public handleSortChanged(sorting: SortingOption[]): void {
    this.activeSorting = sorting;
    this.sortApplied.emit(sorting);
  }

  public applyFilters(): void {
    const filters = this.selectedFilters();
    const { optionKey, startDate, endDate } = filters[OrderFiltersEnum.DATE_CREATED]?.[0] ?? {};

    const payload: OrdersQueryParams = {
      sortingOptions: this.activeSorting,
      creators: filters[OrderFiltersEnum.CREATOR],
      orderStatuses: filters[OrderFiltersEnum.STATUS] as OrderStatus[],
      deliveryTypes: filters[OrderFiltersEnum.DELIVERY_TYPE] as OrderDeliveryTypeEnum[],
      startDate: startDate ?? '',
      endDate: endDate ?? '',
      dateCreated: (optionKey as OrderDateCreatedEnum) ?? '',
    };

    this.ordersFilterService.setFilters(payload);
    this.filtersApplied.emit();
  }

  public resetFilters(): void {
    this.filterSortKey = 'id-asc';
    this.activeSorting = [{ field: 'Id', isDescending: true }];
    this.selectedFilters.set(this.defaultFilters);
    this.ordersFilterService.clearFilters();
    this.applyFilters();
  }

  private syncFromAppliedFilters(filters: OrdersQueryParams): void {
    this.selectedFilters.set({
      [OrderFiltersEnum.CREATOR]: filters.creators ?? [],

      [OrderFiltersEnum.STATUS]: filters.orderStatuses ?? [],

      [OrderFiltersEnum.DELIVERY_TYPE]: filters.deliveryTypes ?? [],

      [OrderFiltersEnum.DATE_CREATED]: filters.dateCreated
        ? [
            {
              optionKey: filters.dateCreated,
              startDate: filters.startDate ?? null,
              endDate: filters.endDate ?? null,
            },
          ]
        : [],
    });

    this.activeSorting = filters.sortingOptions?.length
      ? filters.sortingOptions
      : [{ field: 'Id', isDescending: true }];
  }
}
